import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { X, Download, Paperclip, Send } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface UpdatesModalProps {
  itemId: number | null;
  itemName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface AttachedFile {
  url: string;
  name: string;
  mimeType: string;
  size: number;
}

export function UpdatesModal({ itemId, itemName, isOpen, onClose }: UpdatesModalProps) {
  const [newUpdateContent, setNewUpdateContent] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const utils = trpc.useUtils();

  // Queries
  const { data: updates, isLoading } = trpc.taskManager.updates.listByItem.useQuery(
    { itemId: itemId! },
    { enabled: !!itemId }
  );

  // Mutations
  const createUpdateMutation = trpc.taskManager.updates.create.useMutation({
    onSuccess: async (result) => {
      // Se houver arquivos anexados, criar attachments
      if (attachedFiles.length > 0) {
        for (const file of attachedFiles) {
          await createAttachmentMutation.mutateAsync({
            updateId: result.id,
            fileName: file.name,
            fileUrl: file.url,
            fileSize: file.size,
            mimeType: file.mimeType,
          });
        }
      }
      
      // Sincronizar com coluna Files
      if (attachedFiles.length > 0) {
        await syncFilesColumnMutation.mutateAsync({ itemId: itemId! });
      }
      
      // Invalidar queries para atualização dinâmica
      utils.taskManager.updates.listByItem.invalidate({ itemId: itemId! });
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
      
      setNewUpdateContent('');
      setAttachedFiles([]);
      toast.success('Update adicionado');
    },
    onError: (error) => {
      toast.error('Erro ao adicionar update: ' + error.message);
    },
  });

  const createAttachmentMutation = trpc.taskManager.updateAttachments.create.useMutation();

  const syncFilesColumnMutation = trpc.taskManager.updateAttachments.syncFilesToColumn.useMutation();

  const deleteUpdateMutation = trpc.taskManager.updates.delete.useMutation({
    onSuccess: async () => {
      // Sincronizar com coluna Files após deletar
      await syncFilesColumnMutation.mutateAsync({ itemId: itemId! });
      
      // Invalidar queries para atualização dinâmica
      utils.taskManager.updates.listByItem.invalidate({ itemId: itemId! });
      utils.taskManager.items.listByBoard.invalidate();
      utils.taskManager.items.listByBoardWithValues.invalidate();
      
      toast.success('Update removido');
    },
  });

  const handleSubmit = () => {
    if ((!newUpdateContent.trim() && attachedFiles.length === 0) || !itemId) return;
    
    const content = newUpdateContent.trim() || '(Arquivo anexado)';
    createUpdateMutation.mutate({
      itemId,
      content,
    });
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setAttachedFiles(prev => [...prev, {
          url: data.url,
          name: data.name,
          mimeType: data.mimeType,
          size: data.size,
        }]);
        toast.success('Arquivo anexado');
      } else {
        toast.error('Erro ao fazer upload');
      }
    } catch (error) {
      toast.error('Erro ao fazer upload');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    await handleFileUpload(e.target.files[0]);
    e.target.value = '';
  };

  // Suporte a Ctrl+V para colar imagens
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (!isOpen) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            await handleFileUpload(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  // Drag functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.dialog-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const removeAttachedFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="w-[60vw] max-w-[60vw] max-h-[80vh] flex flex-col"
        style={{
          width: '60vw',
          maxWidth: '60vw',
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default',
        }}
        onMouseDown={handleMouseDown}
        ref={dialogRef}
      >
        <DialogHeader className="dialog-header cursor-grab active:cursor-grabbing">
          <DialogTitle className="flex items-center gap-2">
            <span className="text-lg font-semibold">{itemName}</span>
            <span className="text-sm text-muted-foreground">/ Updates</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {isLoading ? (
            <div className="text-center text-muted-foreground">Carregando...</div>
          ) : updates && updates.length > 0 ? (
            updates.map((update) => {
              const attachments = update.attachments || [];
              return (
                <div key={update.id} className="border rounded-lg p-4 space-y-3 bg-card hover:bg-accent/5 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                        {update.user_name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{update.user_name || 'Usuário'}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(update.created_at), { addSuffix: true, locale: ptBR })}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteUpdateMutation.mutate({ id: update.id })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {update.content && (
                    <div className="text-sm whitespace-pre-wrap pl-10">{update.content}</div>
                  )}
                  
                  {attachments.length > 0 && (
                    <div className="pl-10 space-y-2">
                      {attachments.map((attachment: any) => (
                        <div key={attachment.id} className="flex items-center gap-3 p-2 border rounded bg-background/50">
                          {isImage(attachment.mime_type) ? (
                            <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                              <img
                                src={attachment.file_url}
                                alt={attachment.file_name}
                                className="w-16 h-16 object-cover rounded border hover:opacity-80 transition-opacity"
                              />
                            </a>
                          ) : (
                            <div className="w-16 h-16 flex items-center justify-center bg-muted rounded border">
                              <Paperclip className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <a
                              href={attachment.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline block truncate"
                            >
                              {attachment.file_name}
                            </a>
                            <div className="text-xs text-muted-foreground">
                              {attachment.file_size ? `${(attachment.file_size / 1024).toFixed(1)} KB` : ''}
                            </div>
                          </div>
                          <a
                            href={attachment.file_url}
                            download
                            className="flex-shrink-0"
                          >
                            <Button variant="ghost" size="sm">
                              <Download className="h-4 w-4" />
                            </Button>
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center text-muted-foreground py-8">
              Nenhum update ainda. Seja o primeiro a adicionar!
            </div>
          )}
        </div>

        <div className="border-t pt-4 space-y-3 flex-shrink-0">
          {/* Preview de arquivos anexados */}
          {attachedFiles.length > 0 && (
            <div className="space-y-2">
              {attachedFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-3 p-2 border rounded bg-background/50">
                  {isImage(file.mimeType) ? (
                    <img
                      src={file.url}
                      alt={file.name}
                      className="w-16 h-16 object-cover rounded border"
                    />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center bg-muted rounded border">
                      <Paperclip className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline block truncate"
                    >
                      {file.name}
                    </a>
                    <div className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAttachedFile(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          <Textarea
            ref={textareaRef}
            placeholder="Escreva um update ou cole uma imagem (Ctrl+V)..."
            value={newUpdateContent}
            onChange={(e) => setNewUpdateContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleSubmit();
              }
            }}
            className="min-h-[100px] resize-none"
          />
          
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <input
                type="file"
                id="update-file-upload"
                className="hidden"
                onChange={handleFileInputChange}
                disabled={isUploading}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('update-file-upload')?.click()}
                disabled={isUploading}
              >
                <Paperclip className="h-4 w-4 mr-1" />
                {isUploading ? 'Enviando...' : 'Anexar'}
              </Button>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={(!newUpdateContent.trim() && attachedFiles.length === 0) || createUpdateMutation.isPending}
              size="sm"
            >
              <Send className="h-4 w-4 mr-1" />
              Enviar
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            Dica: Pressione Ctrl+Enter para enviar | Ctrl+V para colar imagens
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
